import React, { useContext, useRef, useState } from 'react'
import { css } from 'emotion'
import { Button, Grid, GridCell, NotificationManagerContext, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import FileInput from 'react-files'
import tinyColor from 'tinycolor2'
import { AttachOutline12 } from 'assets/icons'
import { useMe, useCreateMessage, useCreateMessageReply } from 'src/core/graphql/hooks'
import FileListItemRowSmall from 'src/sites/kits/Files/components/FileListItemRowSmall'
import { useFileUploads } from 'src/sites/kits/Utils'
import HtmlEditor from 'src/sites/kits/Utils/HtmlEditor'
import stylesheet from 'src/sites/kits/Utils/RichText/stylesheet'
import { useGlobalState } from 'src/sites/state'

const MessageInput = ({
   allowFileAttachments,
   hideCancelButton,
   inputExpandedOnStart,
   inputMinHeight,
   inputToolbarConfig,
   messageId,
   onCancel,
   onInputFocus,
   onSubmit,
   placeholderText,
   submitButtonName,
   threadTargetId,
   threadTargetType
}) => {
   const messageInput = useRef()
   const { theme } = useGlobalState()
   const [fileListItems, setFileListItems] = useState([])
   const { showNotification } = useContext(NotificationManagerContext)

   const { isLoggedIn } = useMe()
   const { createMessage, mutating: creatingMessage } = useCreateMessage()
   const { createMessageReply, mutating: creatingReply } = useCreateMessageReply()
   const [inputIsExpanded, setInputIsExpanded] = useState(inputExpandedOnStart)
   const mutating = creatingMessage || creatingReply

   const { uploadFile, uploadsInProgress, resetFileUploadState } = useFileUploads({
      targetType: threadTargetType,
      targetId: threadTargetId,
      purpose: threadTargetType === 'user' ? 'userMessage' : 'initiativeMessage'
   })

   // TODO: Generalize
   // See: PR #448, Issue #452
   const editorEventConfigProps = {
      events: {
         'image.beforeUpload': async (images) => {
            // Only allow file image uploads on message input (not reply input)
            if (!messageId) {
               const currentEditor = messageInput.current.editor
               const { uploadedMediaUrl: imgUrl } = await uploadFile({ file: images[0] })
               currentEditor.image.insert(imgUrl, null, null, currentEditor.image.get())
            }
         }
      }
   }

   const handleCancel = () => {
      setInputIsExpanded(false)

      if (onCancel) {
         onCancel()
      }
   }

   const handleRemoveFile = (fileId) => {
      setFileListItems(prevFiles => prevFiles.filter(file => file.id !== fileId))
   }

   const handleFileUpload = async (filesToUpload) => {
      try {
         const result = await uploadFile({ file: filesToUpload[0] })
         const newFile = {
            id: result.fileId,
            name: filesToUpload[0].name
         }

         setFileListItems(prevFiles => [...prevFiles, newFile])
      } catch (error) {
         showNotification({
            message: error.message,
            title: 'Error!',
            type: 'error'
         })
      } finally {
         resetFileUploadState()
      }
   }

   const handleSubmit = async () => {
      if (uploadsInProgress.length) {
         showNotification({
            message: 'Please wait until your files are done uploading',
            title: 'Error',
            type: 'error'
         })

         return
      }

      const body = messageInput.current.editor.html.get(true)

      // In the event of no input body, let's just let the user continue where they are
      // Don't need an intrusive error message
      if (!body) {
         return
      }

      try {
         if (messageId) {
            await createMessageReply({ body, messageId })
         } else {
            const createPayload = {
               body: [{
                  operation: 'createRichText',
                  createRichText: {
                     body
                  }
               }],
               threadTargetType,
               threadTargetId
            }

            // If files were uploaded
            if (fileListItems.length) {
               const fileIds = fileListItems.map(f => f.id)
               createPayload.body.push({
                  operation: 'createFileList',
                  createFileList: {
                     fileIds
                  }
               })
            }

            await createMessage(createPayload)
         }

         setInputIsExpanded(false)
         resetFileUploadState()
         if (onSubmit) {
            onSubmit()
         }
      } catch (error) {
         showNotification({
            message: error.message,
            title: 'Error!',
            type: 'error'
         })
      }
   }

   return (
      <View>
         {!isLoggedIn && (
            <View
               padding="15px 25px"
               backgroundColor={tinyColor(theme.tmpBodyTextColor).setAlpha(0.05).toRgbString()}
               borderRadius="5px"
               textAlign="center">
               <Text size="2" color={tinyColor(theme.tmpBodyTextColor).setAlpha(0.6).toRgbString()}>
                  You must be logged in to like or post comments
               </Text>
            </View>
         )}
         {isLoggedIn && (
            <View>
               {!inputIsExpanded && (
                  <View
                     onClick={() => {
                        setInputIsExpanded(true)

                        if (onInputFocus) {
                           onInputFocus()
                        }
                        // TODO: Currently, this does not work unless timeout is set.
                        // 0ms does not work
                        setTimeout(() => {
                           messageInput.current.editor.events.focus()
                        }, 100)
                     }}
                     padding="10px 15px"
                     backgroundColor={tinyColor(theme.tmpBodyTextColor).setAlpha(0.1).toRgbString()}
                     borderRadius="5px"
                     transition="100ms background-color"
                     className={css`
                        cursor: text;

                        &:hover {
                           background-color: ${tinyColor(theme.tmpBodyTextColor).setAlpha(0.2).toRgbString()};
                        }
                     `}>
                     <Text size="2" color={theme.tmpBodyTextColor}>
                        {placeholderText}
                     </Text>
                  </View>
               )}
               {inputIsExpanded && (
                  <View
                     float="left"
                     width="100%"
                     borderRadius="5px"
                     backgroundColor="#fafafa">
                     <View
                        float="left"
                        width="100%"
                        backgroundColor="#fff">
                        <Text size="2">
                           <HtmlEditor
                              ref={messageInput}
                              config={{
                                 heightMin: inputMinHeight,
                                 paragraphFormat: { N: 'Body', pre: 'Code' },
                                 zIndex: 'auto',
                                 placeholderText,
                                 ...editorEventConfigProps
                              }}
                              stylesheet={{
                                 ...stylesheet,
                                 // When typing chat message, fon't want images dominating the
                                 // entire chat box
                                 img: {
                                    maxWidth: '100px !important'
                                 }
                              }}
                              toolbarConfig={messageId
                                 ? 'textBasic' // Replies always use textBasic
                                 : inputToolbarConfig // Message is configurable
                              }
                           />
                        </Text>
                     </View>
                     {uploadsInProgress.length > 0 && (
                        <View
                           float="left"
                           width="100%"
                           padding="10px 5px"
                           borderBottom="1px solid #e5e5e5">
                           <Text size="1" color="#888">Uploading...</Text>
                        </View>
                     )}
                     {allowFileAttachments && fileListItems.length > 0 && (
                        <View float="left" width="100%" marginBottom="5px">
                           {fileListItems.map((file, index) => (
                              <FileListItemRowSmall
                                 key={file.id}
                                 id={file.id}
                                 infected={file.infected}
                                 mimetypeMismatch={file.mimetypeMismatch}
                                 mimetypeVerified={file.mimetypeVerified}
                                 name={file.name}
                                 onRemoveButtonClick={handleRemoveFile}
                                 paddingHorizontal="5px"
                                 showRemoveButton
                                 status={file.status}
                              />
                           ))}
                        </View>
                     )}
                     <View
                        float="left"
                        width="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent={allowFileAttachments ? 'space-between' : 'flex-end'}
                        padding="3px 5px">
                        {allowFileAttachments && (
                           <View padding="0px 5px">
                              <FileInput onChange={handleFileUpload}>
                                 <View
                                    display="flex"
                                    alignItems="center"
                                    style={{ cursor: 'pointer' }}>
                                    <View marginRight="15px">
                                       <AttachOutline12 />
                                    </View>
                                    <Text size="1" weight="medium" color="#666">
                                       Attach File
                                    </Text>
                                 </View>
                              </FileInput>
                           </View>
                        )}
                        <View width="100%[a] 200px[b-f]" padding="5px">
                           <Grid columns="2" spacing="5px">
                              <GridCell>
                                 {!hideCancelButton && (
                                    <Button
                                       mode={mutating ? 'disabled' : 'normal'}
                                       width="100%"
                                       onClick={handleCancel}
                                       name="Cancel"
                                       color="#ddd"
                                       textColor="#222"
                                    />
                                 )}
                              </GridCell>
                              <GridCell>
                                 <Button
                                    mode={mutating ? 'loading' : 'normal'}
                                    width="100%"
                                    onClick={handleSubmit}
                                    name={submitButtonName}
                                 />
                              </GridCell>
                           </Grid>
                        </View>
                     </View>
                  </View>
               )}
            </View>
         )}
      </View>
   )
}

MessageInput.propTypes = {
   allowFileAttachments: PropTypes.bool,
   hideCancelButton: PropTypes.bool,
   inputExpandedOnStart: PropTypes.bool,
   inputMinHeight: PropTypes.string,
   inputToolbarConfig: PropTypes.string.isRequired,
   messageId: PropTypes.string,
   onCancel: PropTypes.func,
   onInputFocus: PropTypes.func,
   onSubmit: PropTypes.func,
   placeholderText: PropTypes.string,
   submitButtonName: PropTypes.string,
   threadTargetId: PropTypes.string,
   threadTargetType: PropTypes.string
}

MessageInput.defaultProps = {
   allowFileAttachments: false,
   hideCancelButton: false,
   inputExpandedOnStart: false,
   inputMinHeight: '48px',
   messageId: undefined,
   onCancel: undefined,
   onInputFocus: undefined,
   onSubmit: undefined,
   placeholderText: 'Write something...',
   submitButtonName: 'Post',
   threadTargetId: undefined,
   threadTargetType: 'initiative'
}

export default MessageInput
